// @flow

import React, { Fragment } from 'react';
import { capitalize } from 'lodash';

import {
  type BankFormFields,
  bankAccountTypes,
  bankOwnershipTypes,
} from '@zapier/payment/src/types';
import {
  GridLayout,
  Media,
  SensitiveData,
  TextInput,
} from '@zapier/common-components';
import {
  PaymentFormField,
  PostalCodeInput,
  StateSelect,
} from '@zapier/payment';
import {
  getValue,
  handleChange,
  hasError,
} from '@zapier/payment/src/utils/formHelpers';

import RadioButtonGroup from '../RadioButtonGroup';

const accountTypeOptions = [
  {
    key: bankAccountTypes.checking,
    displayText: capitalize(bankAccountTypes.checking),
  },
  {
    key: bankAccountTypes.savings,
    displayText: capitalize(bankAccountTypes.savings),
  },
];

const ownershipTypeOptions = [
  {
    key: bankOwnershipTypes.business,
    displayText: capitalize(bankOwnershipTypes.business),
  },
  {
    key: bankOwnershipTypes.personal,
    displayText: capitalize(bankOwnershipTypes.personal),
  },
];

type Props = {
  fields: BankFormFields,
  onChangeField: (field: string, value: string) => void,
};

const BankAccountFormFields = ({ fields, onChangeField }: Props) => {
  const ownershipType = getValue('ownershipType', fields);
  return (
    <Media queries={{ small: '(max-width: 750px)' }}>
      {({ small }) => (
        <GridLayout columns={4} gap={10}>
          <PaymentFormField
            id="routingNumber"
            label="Routing Number"
            span={small ? 4 : 2}
          >
            <SensitiveData>
              <TextInput
                hasError={hasError('routingNumber', fields)}
                maxLength={9}
                onChange={handleChange(onChangeField, 'routingNumber', {
                  ignoreWhitespace: true,
                })}
                type="tel"
                value={getValue('routingNumber', fields)}
              />
            </SensitiveData>
          </PaymentFormField>

          <PaymentFormField
            id="accountNumber"
            label="Account Number"
            span={small ? 4 : 2}
          >
            <SensitiveData>
              <TextInput
                hasError={hasError('accountNumber', fields)}
                onChange={handleChange(onChangeField, 'accountNumber', {
                  ignoreWhitespace: true,
                })}
                type="tel"
                value={getValue('accountNumber', fields)}
              />
            </SensitiveData>
          </PaymentFormField>

          <PaymentFormField
            id="accountType"
            isGroup={true}
            label="Account type"
            span={small ? 4 : 2}
          >
            <RadioButtonGroup
              onSelectOption={handleChange(onChangeField, 'accountType')}
              options={accountTypeOptions}
              selectedOption={getValue('accountType', fields)}
            />
          </PaymentFormField>

          <PaymentFormField
            id="ownershipType"
            isGroup={true}
            label="Ownership type"
            span={small ? 4 : 2}
          >
            <RadioButtonGroup
              onSelectOption={handleChange(onChangeField, 'ownershipType')}
              options={ownershipTypeOptions}
              selectedOption={ownershipType}
            />
          </PaymentFormField>

          {ownershipType === bankOwnershipTypes.personal && (
            <Fragment>
              <PaymentFormField id="firstName" label="First name" span={2}>
                <TextInput
                  hasError={hasError('firstName', fields)}
                  onChange={handleChange(onChangeField, 'firstName')}
                  value={getValue('firstName', fields)}
                />
              </PaymentFormField>
              <PaymentFormField id="lastName" label="Last name" span={2}>
                <TextInput
                  hasError={hasError('lastName', fields)}
                  onChange={handleChange(onChangeField, 'lastName')}
                  value={getValue('lastName', fields)}
                />
              </PaymentFormField>
            </Fragment>
          )}

          {ownershipType === bankOwnershipTypes.business && (
            <PaymentFormField id="businessName" label="Business name" span={4}>
              <TextInput
                hasError={hasError('businessName', fields)}
                onChange={handleChange(onChangeField, 'businessName')}
                value={getValue('businessName', fields)}
              />
            </PaymentFormField>
          )}

          <PaymentFormField
            id="streetAddress"
            label="Street address"
            margin="none"
            span={4}
          >
            <SensitiveData>
              <TextInput
                hasError={hasError('streetAddress', fields)}
                onChange={handleChange(onChangeField, 'streetAddress')}
                placeholder="Street and number, P.O. box, c/o."
                value={getValue('streetAddress', fields)}
              />
            </SensitiveData>
          </PaymentFormField>
          <PaymentFormField id="extendedAddress" span={4}>
            <SensitiveData>
              <TextInput
                hasError={hasError('extendedAddress', fields)}
                onChange={handleChange(onChangeField, 'extendedAddress')}
                placeholder="Apartment, suite, unit, building, floor, etc."
                value={getValue('extendedAddress', fields)}
              />
            </SensitiveData>
          </PaymentFormField>
          <PaymentFormField id="locality" label="City" span={small ? 4 : 2}>
            <TextInput
              hasError={hasError('locality', fields)}
              onChange={handleChange(onChangeField, 'locality')}
              value={getValue('locality', fields)}
            />
          </PaymentFormField>
          <PaymentFormField id="region" label="State" span={small ? 4 : 1}>
            <StateSelect
              hasError={hasError('region', fields)}
              onChange={handleChange(onChangeField, 'region')}
              value={getValue('region', fields)}
            />
          </PaymentFormField>
          <PaymentFormField
            id="postalCode"
            label="Zip code"
            span={small ? 4 : 1}
          >
            <PostalCodeInput
              hasError={hasError('postalCode', fields)}
              onChange={handleChange(onChangeField, 'postalCode')}
              value={getValue('postalCode', fields)}
            />
          </PaymentFormField>
        </GridLayout>
      )}
    </Media>
  );
};

export default BankAccountFormFields;
